import {Coordinate} from "ol/coordinate";
import {GritCoordinates, GritCoordinateType} from "./grit.coordinates";
import {GritUtils} from "../../utils/grit/GritUtils";
import {ConfigurationModel} from "../configuration";
import {GritDynamicTableType} from "./grit.enums";
import {Sectorization} from "../sectorization";
import {DeviceSectorizationType} from "./grit.device-model";
import { InterferenceTestModel } from "./grit.interference-test-model";

type NotamCode = {
  position: 23 | 45,
  text: string,
}

type Q = {
  fir : string,
  notam_code : string,
  traffic: string,
  purpose: string,
  scope: string,
  fl : {lower:string, upper: string, buffer: number},
  center: {
    latitude : GritCoordinates,
    longitude : GritCoordinates,
  },
  radius: number,
}

type A = {
  firs : string,
}

type BC = {
  datetime : number;
}

type E = {
  initialText: string,
  area: string,
  areaActivation:  string,
}

export type notamEntity= {
  id: string | number;
  coordinate: Coordinate;
  //Meters
  height: number;
  //NM
  range: number;
  firs?: string[]
  dateStart?: number;
  dateEnd?: number;
  sectorizations? : Sectorization[];
}

export class NOTAM {
  entityRange: number = 0; //NM
  entityHeight: number = 0; //feet
  entitySectorizations: Sectorization[] = [];

  icaoOriginState : string = "";

  itemQ : Q = {
    fir: "",
    notam_code: "",
    traffic: "IV",
    purpose: "NBO",
    scope: "AE",
    fl: {lower: "GND" , upper: "", buffer : 2500}, //Feet
    center: {
      latitude : new GritCoordinates(GritCoordinateType.Latitude),
      longitude : new GritCoordinates(GritCoordinateType.Longitude),
    },
    radius: 0 //NM
  }

  itemA : A = {
    firs: ""
  }

  itemB : BC = {
    datetime: 0
  }

  itemC: BC = {
    datetime: 0
  }

  itemE : E = {
    initialText: "",
    area: "",
    areaActivation: "",
  }

  constructor() {}

  //#region Getters/Setters
  get flLower() {
    if (this.itemQ.fl.lower == "GND"){
      return GritUtils.roundNearestNumber(this.entityHeight, 500) / 100;
    }else{
      return Number(this.itemQ.fl.lower);
    }
  }

  get flUpper() {
    let sum = this.entityHeight + Number(this.itemQ.fl.upper) * 100 + this.itemQ.fl.buffer;
    let upper =  GritUtils.roundNearestNumber(sum, 500) / 100
    return upper > 660 ? 660 : upper;
  }

  get itemBDate() {
    return this.itemB.datetime == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.itemB.datetime)).slice(0, 16)
  }

  set itemBDate(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.itemB.datetime = new Date(dateWithoutTimezone).getTime();
  }

  get itemCDate() {
    return this.itemC.datetime == 0 ? '' : InterferenceTestModel.localDateTimeToISOString(new Date(this.itemC.datetime)).slice(0, 16)
  }

  set itemCDate(date: string) {
    let dateWithoutTimezone = null;
    if (date) {
      dateWithoutTimezone = date.replace(/([+-]\d{2}:\d{2})$/, '');
    }

    // Now parse the date as local time (since the timezone offset is removed)
    this.itemC.datetime = new Date(dateWithoutTimezone).getTime();
  }
  //endregion

  //#region Fill Notam

  static generateNotam(icaoOriginState: string, itType: string, areaType: string, notamEntity : notamEntity) {
    let notam = new NOTAM();
    notam.icaoOriginState = icaoOriginState ?? "";

    notam.processEntity(notamEntity);
    notam.processTypes(itType, areaType);

    return notam;
  }

  private processTypes(itType: string, areaType: string){
    switch (itType){
      case "jamming" :
        this.itemE.initialText = "GNSS MAY BE UNUSABLE WITHIN";
        break;
      case "spoofing" :
        this.itemE.initialText = "GNSS MAY BE MISLEADING WITHIN";
        break;
    }

    switch (areaType){
      case "danger" :
        this.itemQ.notam_code = "QRDCA";
        this.itemE.areaActivation = "DANGER AREA WITHIN";
        break;
      case "restricted" :
        this.itemQ.notam_code = "QRTCA";
        this.itemE.areaActivation = "RESTRICTED AREA WITHIN";
        break;
      default:
        this.itemQ.notam_code = "QGWAU";
        this.itemE.areaActivation = "";
        break;
    }
  }

  private processEntity(entity : notamEntity){
    this.entityRange = entity.range;
    this.entityHeight = GritUtils.roundNearestNumber(entity.height, 50);
    this.entitySectorizations = entity.sectorizations;

    let radius =  GritUtils.roundNearestNumber(this.entityRange, 50)
    this.itemQ.radius = radius > 300 ? 300 : radius

    if (entity.firs?.length == 1){
      this.itemQ.fir = entity.firs[0];
    }else if (this.icaoOriginState != "") {
      this.itemQ.fir = `${this.icaoOriginState}XX`
    }

    this.itemA.firs = entity.firs?.length > 0 ? entity.firs.join(" ") : "";

    this.itemB.datetime = entity.dateStart;
    this.itemC.datetime = entity.dateEnd;

    this.itemQ.center.longitude.value = entity.coordinate[0]
    this.itemQ.center.latitude.value = entity.coordinate[1]


    let upper = Math.round(ConfigurationModel.nmToFeet(entity.range));
    upper = GritUtils.roundNearestNumber(upper, 500);
    this.itemQ.fl.upper = String((upper > 66000 ? 66000 : upper) / 100) ;

    if (entity.sectorizations?.length > 0){
      this.generateAreaDirectional()
    } else {
      this.generateAreaSimple();
    }
  }


  /*
  * @Param All values in meters
  * @Return String representing the area
  * */
  private generateArea(height: number, range: number, angles?: {start: number, end: number}) : string{
    let area = ""

    for (let radius  = 0; radius <= 250 && radius < this.itemQ.radius; radius += 50) {
      let radiusInterval = [radius, radius + 50];

      let lowerRadius = ConfigurationModel.nmToMeters(radiusInterval[0]);
      let upperRadius = ConfigurationModel.nmToMeters(radiusInterval[1]);

      let a1 = GritUtils.FLRadioHorizon(lowerRadius, 0);
      let a2 = GritUtils.FLFreeSpaceRadius(lowerRadius,range, height);

      let b1 = GritUtils.FLRadioHorizon(upperRadius, 0);
      let b2 = GritUtils.FLFreeSpaceRadius(upperRadius,range, height);

      let lowerRadiusFL = Math.min(a1, a2);
      let upperRadiusFL = Math.min(b1, b2);

      let lowerFL = 0;
      if (lowerRadiusFL == -1 || upperRadiusFL == -1) {
        lowerFL = Math.max(lowerRadiusFL, upperRadiusFL);
      } else{
        lowerFL = Math.min(lowerRadiusFL, upperRadiusFL);
      }

      area += `${radiusInterval[0]}NM TO ${radiusInterval[1]}NM RADIUS IMPACT FROM ${this.itemQ.fl.lower == "GND" && lowerFL <= this.flLower ? "GND" : "FL" + lowerFL} UP TO FL${this.flUpper}`;

      if (angles){
        area += ` AND AZIMUTH ${angles.start} TO ${angles.end} DEGREES`
      }

      area += ",\n"
    }

    return area
  }

  private generateAreaSimple(){

    let height = ConfigurationModel.feetToMeters(this.entityHeight);
    let range = ConfigurationModel.nmToMeters(this.entityRange);

    this.itemE.area = this.generateArea(height, range);

    this.itemE.area += `CENTERED AT ${this.itemQ.center.latitude.notamFormat()}${this.itemQ.center.longitude.notamFormat()}`;
  }

  private generateAreaDirectional() {
    let areas = {
      main : {
        range: this.entityRange,
        angles: [],
        minAngle: 360,
        maxAngle: 0,
      },
      side : {
        range: 0,
        angles: [],
        minAngle: 360,
        maxAngle: 0,
      }
    }

    for (let sect of this.entitySectorizations){
      let interval = [sect.minangle, sect.maxangle];
      let range = sect.doc;

      if (sect.id == DeviceSectorizationType.MAIN){
        if (areas.main.range < range) areas.main.range = range;
        areas.main.angles.push(interval);
      }
      else {
        if (areas.side.range < range) areas.side.range = range;
        areas.side.angles.push(interval);
      }
    }

    //Merge main angles
    areas.main.angles = GritUtils.mergeCyclicAngles(areas.main.angles);
    areas.side.angles = GritUtils.mergeCyclicAngles(areas.side.angles);

    if (areas.main.angles != null) {
      areas.side.angles = [areas.main.angles[1], areas.main.angles[0]];
    }
    else if (areas.side.angles != null) {
      areas.main.angles = [areas.side.angles[1], areas.side.angles[0]];
    }
    else {
      areas.main.angles = ["Xi", "Xf"];
      areas.side.angles = ["Xf", "Xi"];
    }

    let range = ConfigurationModel.nmToMeters(areas.main.range);
    let height = ConfigurationModel.feetToMeters(this.entityHeight);

    this.itemE.area = "";
    this.itemE.area += `${areas.side.range}NM RADIUS FROM ${this.itemQ.fl.lower == "GND" ? "GND" : "FL" + this.flLower} TO FL${this.flUpper} AND AZIMUTH ${areas.side.angles[0]} TO ${areas.side.angles[1]} DEGREES,\n`;

    this.itemE.area += this.generateArea(height, range, {start: areas.main.angles[0], end: areas.main.angles[1]})

    this.itemE.area += `CENTERED AT ${this.itemQ.center.latitude.notamFormat()}${this.itemQ.center.longitude.notamFormat()}`;
  }

  updateArea(){
    if (this.entitySectorizations?.length > 0){
      this.generateAreaDirectional();
    }
    else{
      this.generateAreaSimple();
    }
  }


  decodeNotamCode() : string {
    let block1 = this.itemQ.notam_code.substring(1, 3);
    let block2 = this.itemQ.notam_code.substring(3, 5);

    return `${NOTAM.notamCodeList[block1].text} ${NOTAM.notamCodeList[block2].text}`.toUpperCase()
  }
  //endregion

  //#region Output
  formatDate(dateInt: number): string {
    let date = new Date(dateInt)
    const year = date.getFullYear ().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
  }

  generate() : string {
    let message = "";

    //Item Q
    message += "Q) "
    message += `${this.itemQ.fir}/${this.itemQ.notam_code}/${this.itemQ.traffic}/${this.itemQ.purpose}/${this.itemQ.scope}/`;
    message += `${this.itemQ.fl.lower}/${this.flUpper}/`
    message += `${this.itemQ.center.latitude.notamFormat()}${this.itemQ.center.longitude.notamFormat()}`
    message += `${this.itemQ.radius.toFixed(0).padStart(3, "0")}\n`

    //Item A
    message += `A) ${this.itemA.firs} `

    //Item B
    message += `B) ${this.formatDate(this.itemB.datetime)} `

    //Item C
    if (this.itemC.datetime > -1 ) {
      message += `C) ${this.formatDate(this.itemC.datetime)}\n`
    }else{
      message += "\n"
    }

    //Item E
    message += `E) `
    message += `${this.itemE.initialText != "" ? `${this.itemE.initialText}:\n` : ''}`
    message += `${this.itemE.areaActivation != "" ? `${this.itemE.areaActivation}:\n` : ''}`
    message += `${this.itemE.area != "" ? this.itemE.area : "NO AREA DEFINED"}\n`
    message += `${this.itemE.areaActivation != "" ? 'ACTIVATED\n' : ''}`

    return message
  }
  //endregion

  static notamCodeList : Record<string, NotamCode> = {
    GW : {
      position: 23,
      text: "GNSS area-wide operations (specify operation)",
    },
    GA : {
      position: 23,
      text: "GNSS airfield-specific operations (specify operation)",
    },
    RD : {
      position: 23,
      text: "Danger area (specify)",
    },
    RT : {
      position: 23,
      text: "Temporary restricted area (specify area)",
    },

    AU : {
      position: 45,
      text: "Not available (specify reason if appropriate)",
    },
    CA : {
      position: 45,
      text: "Activated",
    },
    AK : {
      position: 45,
      text: "Previously promulgated shutdown has been canceled",
    },
    AL : {
      position: 45,
      text: "Operative (or reoperative) subject to previously published limitations/conditions",
    },
    AO : {
      position: 45,
      text: "Operational",
    },
    CC : {
      position: 45,
      text: "Completed",
    },
    CN : {
      position: 45,
      text: "Canceled",
    },
    HV : {
      position: 45,
      text: "Work completed",
    },
    XX : {
      position: 45,
      text: "Other reasons (specify)",
    },
  }
}
